import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Button,
  Stack,
  Divider,
  CircularProgress,
  Badge,
} from '@mui/material';
import {
  Delete,
  MarkEmailRead,
  DoneAll,
  Refresh,
  Inbox,
  Outbox,
  Archive,
  Restore,
} from '@mui/icons-material';
import { staffNotificationsAPI } from '../services/api';

function formatDateTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function typeChip(type) {
  const map = {
    application: { label: '배차신청', color: 'info' },
    new_application: { label: '배차신청', color: 'info' },
    delivery_started: { label: '배송시작', color: 'warning' },
    delivery_completed: { label: '배송완료', color: 'success' },
    status_update: { label: '상태변경', color: 'warning' },
    broadcast_start: { label: '배차호출', color: 'primary' },
    assignment_approved: { label: '배차확정', color: 'success' },
    broadcast_cancel: { label: '호출취소', color: 'default' },
    assignment_cancel: { label: '배차취소', color: 'error' },
  };
  const m = map[type] || { label: type || '알림', color: 'default' };
  return <Chip label={m.label} color={m.color} size="small" sx={{ fontWeight: 600 }} />;
}

export default function Notifications() {
  const [tab, setTab] = useState(0); // 0: incoming, 1: outgoing, 2: archived
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({ incoming_unread: 0, outgoing_unread: 0, archived: 0 });

  const isArchiveTab = tab === 2;
  const direction = tab === 0 ? 'incoming' : tab === 1 ? 'outgoing' : null;

  const loadCounts = useCallback(async () => {
    try {
      const [unreadRes, archivedRes] = await Promise.all([
        staffNotificationsAPI.getUnreadCount(),
        staffNotificationsAPI.getArchivedCount().catch(() => ({ data: { count: 0 } })),
      ]);
      setCounts({
        incoming_unread: unreadRes.data.incoming_unread || 0,
        outgoing_unread: unreadRes.data.outgoing_unread || 0,
        archived: archivedRes.data.count || 0,
      });
    } catch (err) {
      // ignore
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (isArchiveTab) params.archived = 1;
      else params.direction = direction;
      const res = await staffNotificationsAPI.getAll(params);
      setRows(res.data.data || []);
    } catch (err) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [direction, isArchiveTab]);

  useEffect(() => {
    load();
    loadCounts();
  }, [load, loadCounts]);

  const handleMarkRead = async (id) => {
    try {
      await staffNotificationsAPI.markRead(id);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, is_read: 1 } : r)));
      loadCounts();
    } catch (err) {
      alert('읽음 처리 실패');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await staffNotificationsAPI.markAllRead(direction);
      setRows((prev) => prev.map((r) => ({ ...r, is_read: 1 })));
      loadCounts();
    } catch (err) {
      alert('전체 읽음 처리 실패');
    }
  };

  const handleArchive = async (id) => {
    if (!window.confirm('이 알림을 보관함으로 옮길까요? (보관함에서 복구 가능)')) return;
    try {
      await staffNotificationsAPI.delete(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
      loadCounts();
    } catch (err) {
      alert('보관 처리 실패');
    }
  };

  const handleRestore = async (id) => {
    try {
      await staffNotificationsAPI.restore(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
      loadCounts();
    } catch (err) {
      alert('복구 실패');
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          알림 게시판
        </Typography>
        <Stack direction="row" spacing={1}>
          {!isArchiveTab && (
            <Button
              startIcon={<DoneAll />}
              variant="outlined"
              size="small"
              onClick={handleMarkAllRead}
              disabled={rows.length === 0}
            >
              전체 읽음
            </Button>
          )}
          <Button
            startIcon={<Refresh />}
            variant="outlined"
            size="small"
            onClick={() => { load(); loadCounts(); }}
          >
            새로고침
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
          <Tab
            icon={
              <Badge badgeContent={counts.incoming_unread} color="error">
                <Inbox />
              </Badge>
            }
            iconPosition="start"
            label="받은 알림"
          />
          <Tab
            icon={
              <Badge badgeContent={counts.outgoing_unread} color="error">
                <Outbox />
              </Badge>
            }
            iconPosition="start"
            label="보낸 알림"
          />
          <Tab
            icon={
              <Badge badgeContent={counts.archived} color="default" max={999}>
                <Archive />
              </Badge>
            }
            iconPosition="start"
            label="보관함"
          />
        </Tabs>
      </Paper>

      {isArchiveTab && (
        <Box sx={{ mb: 1, px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            ※ "삭제"한 알림이 모이는 곳입니다. 읽은 알림은 30일 후 자동으로 이곳으로 이동합니다.
          </Typography>
        </Box>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            {isArchiveTab ? '보관된 알림이 없습니다.' : '알림이 없습니다.'}
          </Box>
        ) : (
          <List disablePadding>
            {rows.map((row, idx) => (
              <React.Fragment key={row.id}>
                {idx > 0 && <Divider />}
                <ListItem
                  sx={{
                    backgroundColor: isArchiveTab
                      ? '#fafafa'
                      : (row.is_read ? 'transparent' : '#e3f2fd'),
                    '&:hover': { backgroundColor: row.is_read || isArchiveTab ? '#f0f0f0' : '#bbdefb' },
                    alignItems: 'flex-start',
                    py: 1.5,
                  }}
                  secondaryAction={
                    <Stack direction="row" spacing={0.5}>
                      {isArchiveTab ? (
                        <IconButton
                          edge="end"
                          size="small"
                          title="복구"
                          color="primary"
                          onClick={() => handleRestore(row.id)}
                        >
                          <Restore fontSize="small" />
                        </IconButton>
                      ) : (
                        <>
                          {!row.is_read && (
                            <IconButton
                              edge="end"
                              size="small"
                              title="읽음 처리"
                              onClick={() => handleMarkRead(row.id)}
                            >
                              <MarkEmailRead fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            edge="end"
                            size="small"
                            title="삭제 (보관함으로 이동)"
                            onClick={() => handleArchive(row.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        {typeChip(row.type)}
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: row.is_read ? 500 : 700 }}
                        >
                          {row.title}
                        </Typography>
                        {!row.is_read && !isArchiveTab && (
                          <Chip label="NEW" size="small" color="error" sx={{ height: 18, fontSize: 10 }} />
                        )}
                      </Stack>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                          {row.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(row.created_at)}
                          {row.related_driver_name ? ` · 운전자: ${row.related_driver_name}` : ''}
                          {row.related_order_id ? ` · 주문 #${row.related_order_id}` : ''}
                          {isArchiveTab && row.archived_at ? ` · 보관: ${formatDateTime(row.archived_at)}` : ''}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
}
